import { Grid, Stack } from '@mui/material';
import { SummaryCard } from '@side/pantry';
import { useState } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useAgentDslFilters } from '@/common/hooks/useAgentDslFilters';
import { formatAddress } from '@/common/utils/address';
import { formatPhoneNumber } from '@/common/utils/phoneNumber';
import { ReadOnlyField } from '@/components/ReadOnlyField';
import { type AgentProfileFields, useAgentProfile } from '$agent-profiles';
import { AgentAutocomplete, type AgentSelectOption } from '$form-fields';
import type { CreateOfferFormData } from '../types';

const SellerAgentSummary = ({ profile }: { profile: AgentProfileFields }) => (
  <SummaryCard title={null}>
    <Grid container spacing={4.5}>
      <Grid size={{ xs: 6, lg: 4 }}>
        <ReadOnlyField label="Email" value={profile.email || null} />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <ReadOnlyField
          label="Cell Phone #"
          value={
            profile.cellPhone ? formatPhoneNumber(profile.cellPhone) : null
          }
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <ReadOnlyField
          label="Agent License #"
          value={profile.licenseNumber || null}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <ReadOnlyField
          label="Brokerage License #"
          value={profile.brokerageLicenseNumber || null}
        />
      </Grid>
      <Grid size={{ xs: 6, lg: 4 }}>
        <ReadOnlyField
          label="Brokerage Name"
          value={profile.companyName || null}
        />
      </Grid>
      <Grid size={12}>
        <ReadOnlyField
          label="Address"
          value={
            formatAddress({
              street1: profile.street1 ?? undefined,
              street2: profile.street2 ?? undefined,
              city: profile.city ?? undefined,
              state: profile.state ?? undefined,
              zip: profile.zipCode ?? undefined,
            }) || null
          }
        />
      </Grid>
    </Grid>
  </SummaryCard>
);

export const SellerAgentAutocomplete = () => {
  const state = useWatch<CreateOfferFormData, 'state'>({ name: 'state' });
  const agentDslFilters = useAgentDslFilters({
    licenseStates: state ? [state] : undefined,
  });

  const {
    field,
    fieldState: { error, invalid },
  } = useController<CreateOfferFormData, 'sellerAgent'>({
    name: 'sellerAgent',
    rules: { required: 'Required' },
  });

  // MUI resets the Autocomplete input on referential value changes, so keep the
  // selected option in local state.
  const [selectedOption, setSelectedOption] = useState<
    AgentSelectOption | undefined
  >(field.value);

  const { data: profile } = useAgentProfile({
    userId: selectedOption?.userIdentityId,
    teamId: selectedOption?.teamIdentityId,
    skip: !selectedOption,
    keepPreviousData: true,
  });

  return (
    <Stack sx={{ gap: 1 }}>
      <AgentAutocomplete
        id="seller-agent"
        selectedAgent={selectedOption}
        onChange={(_, value) => {
          if (!value) return;
          setSelectedOption(value);
          field.onChange(value);
        }}
        TextFieldProps={{
          name: 'sellerAgent',
          label: 'Agent Name',
          placeholder: 'Search and select',
          required: true,
          highlight: selectedOption ? undefined : 'secondary',
          helperText: error?.message,
          error: invalid,
          onBlur: field.onBlur,
        }}
        filters={agentDslFilters}
      />
      {profile && <SellerAgentSummary profile={profile} />}
    </Stack>
  );
};
